import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { adminApi, type ConstantMeta } from '@/services/adminApi';
import { ApiClientError } from '@/services/apiClient';
import type { RootState } from '@/store';

/**
 * Mirrors the backend registry's defaults (in `constants.registry.ts`). Used
 * as the fallback before `fetchConstants` resolves and during failure modes
 * (offline boot, transient 5xx). Keep in sync with the backend defaults —
 * drift only matters until the first successful fetch.
 */
const REGISTRY_DEFAULTS = {
  nextEpisodeLeadSeconds: 60,
  historyLimit: 20,
  resumeMinProgressMs: 5000,
  nearCompleteThreshold: 95,
  lobbyChatRateLimitPerSec: 10,
  lobbyStaleSweepMinutes: 30,
  failoverBufferThresholdSeconds: 15,
  failoverSegment4xxThreshold: 2,
  failoverErrorWindowMs: 30_000,
} as const;

export type ConstantsValues = Record<string, number>;
export type ConstantsStatus = 'idle' | 'loading' | 'ready' | 'error';

export interface ConstantsState {
  values: ConstantsValues;
  registry: ConstantMeta[];
  status: ConstantsStatus;
  error: string | null;
  /** Per-key save state for the admin UI. */
  saving: Record<string, 'idle' | 'saving' | 'saved' | 'error'>;
}

const initialState: ConstantsState = {
  values: { ...REGISTRY_DEFAULTS },
  registry: [],
  status: 'idle',
  error: null,
  saving: {},
};

function describeError(err: unknown): string {
  if (err instanceof ApiClientError) return err.message;
  if (err instanceof Error) return err.message;
  return 'Unknown error';
}

export const fetchConstants = createAsyncThunk<
  { values: ConstantsValues; registry: ConstantMeta[] },
  void,
  { rejectValue: string }
>('constants/fetch', async (_, { rejectWithValue }) => {
  try {
    const res = await adminApi.getConstants();
    return { values: res.values, registry: res.registry };
  } catch (err) {
    return rejectWithValue(describeError(err));
  }
});

export const updateConstant = createAsyncThunk<
  { key: string; value: number },
  { key: string; value: number },
  { rejectValue: { key: string; message: string } }
>('constants/update', async ({ key, value }, { rejectWithValue, dispatch }) => {
  try {
    const out = await adminApi.updateConstant(key, value);
    // Refetch the canonical map so any cached overrides on the server side
    // are reflected (and so the admin sees a true round-tripped state).
    void dispatch(fetchConstants());
    return out;
  } catch (err) {
    return rejectWithValue({ key, message: describeError(err) });
  }
});

const constantsSlice = createSlice({
  name: 'constants',
  initialState,
  reducers: {
    clearSavedFlag(state, action: { payload: string }) {
      state.saving[action.payload] = 'idle';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchConstants.pending, (state) => {
        state.status = state.registry.length > 0 ? 'ready' : 'loading';
        state.error = null;
      })
      .addCase(fetchConstants.fulfilled, (state, action) => {
        // Merge: registry defaults first, server values override. This means
        // if the registry adds a new key the frontend doesn't blow up before
        // its bundle is updated.
        state.values = { ...REGISTRY_DEFAULTS, ...action.payload.values };
        state.registry = action.payload.registry;
        state.status = 'ready';
      })
      .addCase(fetchConstants.rejected, (state, action) => {
        state.status = 'error';
        state.error = action.payload ?? action.error.message ?? 'Failed to load constants';
      })
      .addCase(updateConstant.pending, (state, action) => {
        state.saving[action.meta.arg.key] = 'saving';
        // Optimistic: reflect the new value immediately. fetchConstants
        // dispatched in the thunk will overwrite with the canonical server
        // value on success; the rejected handler rolls back from the previous
        // value below.
        state.values[action.meta.arg.key] = action.meta.arg.value;
      })
      .addCase(updateConstant.fulfilled, (state, action) => {
        state.saving[action.payload.key] = 'saved';
        state.values[action.payload.key] = action.payload.value;
      })
      .addCase(updateConstant.rejected, (state, action) => {
        const key = action.payload?.key ?? action.meta.arg.key;
        state.saving[key] = 'error';
        state.error = action.payload?.message ?? action.error.message ?? 'Failed to save constant';
        // No rollback — fetchConstants will rehydrate from the server on the
        // next page load. Surfacing the error to the admin is more useful
        // than silently snapping the select back.
      });
  },
});

export const { clearSavedFlag } = constantsSlice.actions;
export default constantsSlice.reducer;

/** Selector that returns the value-or-default for a given key. */
export function selectConstant(state: RootState, key: string): number {
  return state.constants.values[key] ?? REGISTRY_DEFAULTS[key as keyof typeof REGISTRY_DEFAULTS] ?? 0;
}

export { REGISTRY_DEFAULTS as CONSTANTS_DEFAULTS };
